import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../supabaseClient';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Alert, alpha, useTheme, IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  AutoFixHigh as AutoFillIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const STATUS_COLORS = {
  draft: 'default',
  pending_approval: 'warning',
  approved: 'success',
  in_production: 'info',
  shipped: 'primary',
  delivered: 'success',
  cancelled: 'error',
};

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatCurrency = (v) =>
  v != null ? `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

export default function AutofillOrderDialogue({ open, onClose, onAutofill, companyId }) {
  const theme = useTheme();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const fetchOrders = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('customer_purchase_orders')
        .select('id, po_number, customer_name, customer_company, order_date, status, total_amount, requested_delivery_date')
        .eq('company_id', companyId)
        .neq('status', 'cancelled')
        .order('created_at', { ascending: false })
        .limit(200);
      if (err) throw err;
      setOrders(data || []);
    } catch (e) {
      setError('Failed to load orders: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedId(null);
      setError(null);
      fetchOrders();
    }
  }, [open, fetchOrders]);

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    return (
      (o.po_number || '').toLowerCase().includes(q) ||
      (o.customer_name || '').toLowerCase().includes(q) ||
      (o.customer_company || '').toLowerCase().includes(q)
    );
  });

  const handleSelect = async (order) => {
    setSelectedId(order.id);
    setFetching(true);
    setError(null);
    try {
      // Fetch po_creation for this order
      const { data: poCreation, error: pcErr } = await supabase
        .from('po_creation')
        .select('*')
        .eq('purchase_order_id', order.id)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      if (pcErr && pcErr.code !== 'PGRST116') throw pcErr;

      // Fetch line items (exclude lot/batch/mfg/exp)
      let lineItems = [];
      if (poCreation?.id) {
        const { data: items, error: itemsErr } = await supabase
          .from('po_creation_items')
          .select(
            'id, product_id, variant_id, product_name, variant_name, description, ' +
            'quantity, unit_type, units_per_case, unit_price, line_total, line_order'
          )
          .eq('po_creation_id', poCreation.id)
          .eq('line_status', 'active')
          .order('line_order');
        if (itemsErr) throw itemsErr;
        lineItems = (items || []).map(item => ({
          product_id: item.product_id || null,
          variant_id: item.variant_id || null,
          product_name: item.product_name || '',
          variant_name: item.variant_name || '',
          description: item.description || '',
          quantity: item.quantity || 1,
          unit_type: item.unit_type || 'Case',
          units_per_case: item.units_per_case || 1,
          unit_price: item.unit_price || 0,
          line_total: item.line_total || 0,
          total_price: item.line_total || 0,
          line_order: item.line_order || 0,
        }));
      }

      // Fetch BOL number from delivery info
      const { data: bolData } = await supabase
        .from('po_delivery_information')
        .select('bol_number')
        .eq('purchase_order_id', order.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Build invoice autofill data
      const invoiceData = {
        purchase_order_number: order.po_number || '',
        bill_of_lading_number: bolData?.bol_number || '',
        customer_id: poCreation?.customer_id || '',
        customer_name: poCreation?.customer_name || order.customer_name || '',
        customer_company: poCreation?.customer_company || order.customer_company || '',
        billing_street: poCreation?.billing_street || '',
        billing_city: poCreation?.billing_city || '',
        billing_state: poCreation?.billing_state || '',
        billing_zip: poCreation?.billing_zip || '',
        billing_country: poCreation?.billing_country || '',
        shipping_street: poCreation?.shipping_street || '',
        shipping_city: poCreation?.shipping_city || '',
        shipping_state: poCreation?.shipping_state || '',
        shipping_zip: poCreation?.shipping_zip || '',
        shipping_country: poCreation?.shipping_country || '',
        notes: poCreation?.customer_notes || '',
        special_instructions: poCreation?.special_instructions || '',
        internal_notes: poCreation?.internal_notes || '',
        subtotal: parseFloat(poCreation?.subtotal) || 0,
        discount: parseFloat(poCreation?.discount_percent) || 0,
        discount_type: poCreation?.discount_type || 'percent',
        discount_amount: parseFloat(poCreation?.discount_amount) || 0,
        tax_rate: parseFloat(poCreation?.tax_rate) || 0,
        tax_amount: parseFloat(poCreation?.tax_amount) || 0,
        shipping_fee: parseFloat(poCreation?.shipping_fee) || 0,
        handling_fee: parseFloat(poCreation?.handling_fee) || 0,
        credit_applied: parseFloat(poCreation?.credit_applied) || 0,
        credit_notes: poCreation?.credit_notes || '',
        total_before_credit: parseFloat(poCreation?.total_before_credit) || 0,
        total_amount: parseFloat(poCreation?.total_amount) || 0,
        amount_due: parseFloat(poCreation?.amount_due) || 0,
        price_list_id: poCreation?.price_list_id || '',
        price_list_name: poCreation?.price_list_name || '',
        payment_terms: poCreation?.payment_terms || 'Net 30',
        payment_method: poCreation?.payment_method || '',
        late_fee_rate: parseFloat(poCreation?.late_fee_rate) || 0,
        early_payment_discount: parseFloat(poCreation?.early_payment_discount) || 0,
        early_payment_days: parseInt(poCreation?.early_payment_days) || 0,
        terms_and_conditions: poCreation?.terms_and_conditions || '',
        prepared_by: poCreation?.prepared_by || null,
      };

      onAutofill(invoiceData, lineItems);
      onClose();
    } catch (e) {
      setError('Failed to load order data: ' + e.message);
      setSelectedId(null);
    } finally {
      setFetching(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={fetching ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '82vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            p: 1, borderRadius: 2,
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
          }}>
            <AssignmentIcon sx={{ fontSize: 22, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={500} sx={{ lineHeight: 1.2 }}>Autofill from Order</Typography>
            <Typography variant="body2" color="text.secondary">Select a proforma invoice to import data</Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={fetching} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by PO#, customer name or company..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2 }
          }}
        />
      </Box>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mx: 3, mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={36} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {search ? 'No orders match your search' : 'No orders found'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.grey[100], 0.95), fontSize: '0.8rem' }}>PI #</TableCell>
                  <TableCell sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.grey[100], 0.95), fontSize: '0.8rem' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.grey[100], 0.95), fontSize: '0.8rem' }}>Order Date</TableCell>
                  <TableCell sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.grey[100], 0.95), fontSize: '0.8rem' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.grey[100], 0.95), fontSize: '0.8rem' }} align="right">Total</TableCell>
                  <TableCell sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.grey[100], 0.95), fontSize: '0.8rem' }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(order => {
                  const isLoading = fetching && selectedId === order.id;
                  return (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{
                        cursor: fetching ? 'not-allowed' : 'pointer',
                        opacity: fetching && selectedId !== order.id ? 0.5 : 1,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                        bgcolor: selectedId === order.id ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                      }}
                      onClick={() => !fetching && handleSelect(order)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} color="primary.main">
                          {order.po_number || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{order.customer_name || '—'}</Typography>
                        {order.customer_company && (
                          <Typography variant="caption" color="text.secondary">{order.customer_company}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(order.order_date)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status?.replace(/_/g, ' ') || 'draft'}
                          size="small"
                          color={STATUS_COLORS[order.status] || 'default'}
                          sx={{ textTransform: 'capitalize', fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>{formatCurrency(order.total_amount)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Import this order">
                          <span>
                            <IconButton
                              size="small"
                              disabled={fetching}
                              onClick={e => { e.stopPropagation(); if (!fetching) handleSelect(order); }}
                              sx={{ color: 'primary.main' }}
                            >
                              {isLoading
                                ? <CircularProgress size={16} />
                                : <AutoFillIcon fontSize="small" />
                              }
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {filtered.length} order{filtered.length !== 1 ? 's' : ''} {search ? 'found' : 'available'}
        </Typography>
        <Button onClick={onClose} disabled={fetching} sx={{ borderRadius: 2, textTransform: 'none' }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
